export const HANDOFF_READY_MESSAGE = 'nova:handoff:ready';
export const HANDOFF_TOKEN_MESSAGE = 'nova:handoff:token';
// Value of the ?handoff query param that tells the destination to wait for a token from its opener.
export const OPENER_HANDOFF_QUERY_VALUE = 'opener';

const SENDER_TIMEOUT_MS = 30000;
const RECEIVER_TIMEOUT_MS = 5000;

function isMessageFrom({ event, source, origin, type }) {
  return event.source === source && event.origin === origin && event.data?.type === type;
}

/**
 * Opens `url` in a new window and delivers an access token to it over postMessage once the
 * destination reports ready. The window is opened synchronously so the call must run inside the
 * user's click handler, before any await. `getAccessToken` runs after the window exists.
 * @param {{ url: string, getAccessToken: () => Promise<string | undefined>, timeoutMs?: number }} params
 * @returns {Promise<boolean>} true when the window was opened and token delivery is armed
 */
export async function openWindowWithHandoff({
  url,
  getAccessToken,
  timeoutMs = SENDER_TIMEOUT_MS
}) {
  const popup = window.open('', '_blank');
  if (!popup) {
    return false;
  }

  let accessToken;
  try {
    accessToken = await getAccessToken();
  } catch (error) {
    popup.close();
    throw error;
  }
  if (!accessToken) {
    popup.close();
    return false;
  }

  const destinationOrigin = new URL(url).origin;

  function stopListening() {
    clearTimeout(timeoutId);
    window.removeEventListener('message', onMessage);
  }

  function onMessage(event) {
    if (
      !isMessageFrom({
        event,
        source: popup,
        origin: destinationOrigin,
        type: HANDOFF_READY_MESSAGE
      })
    ) {
      return;
    }
    stopListening();
    popup.postMessage({ type: HANDOFF_TOKEN_MESSAGE, token: accessToken }, destinationOrigin);
  }

  const timeoutId = setTimeout(stopListening, timeoutMs);
  window.addEventListener('message', onMessage);
  popup.location.href = url;
  return true;
}

/**
 * Asks the window that opened this one for an access token and waits for it.
 * @param {{ openerOrigin: string, timeoutMs?: number }} params openerOrigin is the opener's URL;
 * only its origin is used.
 * @returns {Promise<string | null>} the token, or null without an opener or when none arrives in time
 */
export function receiveHandoffFromOpener({ openerOrigin, timeoutMs = RECEIVER_TIMEOUT_MS }) {
  const opener = window.opener;
  if (!opener) {
    return Promise.resolve(null);
  }

  const expectedOrigin = new URL(openerOrigin).origin;

  return new Promise(resolve => {
    function finish(token) {
      clearTimeout(timeoutId);
      window.removeEventListener('message', onMessage);
      resolve(token);
    }

    function onMessage(event) {
      if (
        !isMessageFrom({
          event,
          source: opener,
          origin: expectedOrigin,
          type: HANDOFF_TOKEN_MESSAGE
        })
      ) {
        return;
      }
      const { token } = event.data;
      if (typeof token !== 'string' || token.length === 0) {
        return;
      }
      finish(token);
    }

    const timeoutId = setTimeout(() => finish(null), timeoutMs);
    window.addEventListener('message', onMessage);
    opener.postMessage({ type: HANDOFF_READY_MESSAGE }, expectedOrigin);
  });
}
